// Primer paso del formulario de creación de sucursal - República Dominicana
// Versión simplificada: NO incluye persona jurídica/física, CUIT, AFIP, provincia ni ciudad

import { FormHelperText, IconButton, Switch, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoIcon from "../../../../icons/InfoIcon";
import { BranchSettings } from "../../../../../services/dto/request/branches-request-dto";
import { BranchBasicInfo } from "../../../../../services/dto/request/branches-request-dto";
import { sanitizeArgBranchNameInput } from "../../../../../utils/helpers";

const textFieldStyles = {
  "& .MuiInput-root": {
    color: "#000",
    fontFamily: "Montserrat",
    "&:before": {
      borderBottom: "1px solid #838383",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#898989",
    },
  },
  "& label.Mui-focused": {
    color: "#7e7e7e",
  },
  "& .MuiInputLabel-standard": {
    color: "#000",
    fontWeight: "400",
    fontFamily: "Montserrat",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#838383",
  },
  "& .Mui-error": {
    color: "#d32f2f",
  },
  "& .MuiFormLabel-root.Mui-error": {
    color: "#d32f2f",
  },
  "& .MuiInput-underline.Mui-error:before": {
    borderBottomColor: "#d32f2f",
  },
  "& .MuiInput-underline.Mui-error:after": {
    borderBottomColor: "#d32f2f",
  },
};

const switchStyles = {
  "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
    bgcolor: "#EF0BB8",
  },
  "& .MuiSwitch-track": {
    bgcolor: "#898989",
  },
  "& .MuiSwitch-thumb": {
    bgcolor: "#EF0BB8",
  },
};

export interface BranchInfoStepDOProps {
  branchBasicInfo: BranchBasicInfo;
  branchSettings: BranchSettings;
  errors: Record<string, string>;
  handleBranchBasicInfoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBranchSettingsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSwitchChange: (e: React.ChangeEvent<HTMLInputElement>, type: "branchConfig" | "branchBasicInfo" | "manager") => void;
  withSplitTip: boolean;
  disabled: boolean;
  isEditMode?: boolean;
}

/**
 * Formulario de información de sucursal para República Dominicana
 * NO incluye: persona jurídica/física, CUIT, AFIP, provincia ni ciudad
 */
export function BranchInfoStepDO({
  branchBasicInfo,
  branchSettings,
  errors,
  handleBranchBasicInfoChange,
  handleBranchSettingsChange,
  handleSwitchChange,
  withSplitTip,
  disabled,
  isEditMode = false,
}: BranchInfoStepDOProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      {/* RD no requiere cuenta de propinas - campo oculto */}
      
      <TextField
        type="text"
        name="name"
        value={branchBasicInfo.name}
        onChange={handleBranchBasicInfoChange}
        error={!!errors.name}
        helperText={errors.name}
        label={t("branches.create.name")}
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          input.value = sanitizeArgBranchNameInput(input.value);
        }}
        variant="standard"
        disabled={disabled || isEditMode}
        required
        fullWidth
        sx={textFieldStyles}
      />

      {/* Campo nombre de fantasía - Solo en modo de edición */}
      {isEditMode && (
        <TextField
          type="text"
          name="fantasyName"
          value={branchBasicInfo.fantasyName || ""}
          onChange={handleBranchBasicInfoChange}
          error={!!errors.fantasyName}
          helperText={errors.fantasyName}
          label={t("branches.edit.fantasy-name")}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            input.value = sanitizeArgBranchNameInput(input.value);
          }}
          variant="standard"
          disabled={disabled}
          fullWidth
          sx={textFieldStyles}
        />
      )}
      
      <TextField
        type="text"
        name="address"
        value={branchBasicInfo.address}
        onChange={handleBranchBasicInfoChange}
        required
        disabled={disabled}
        error={!!errors.address}
        helperText={errors.address}
        label={t("branches.create.address")}
        variant="standard"
        sx={textFieldStyles}
      />
      
      <TextField
        type="text"
        name="phone"
        value={branchBasicInfo.phone}
        onChange={handleBranchBasicInfoChange}
        required
        error={!!errors.phone}
        helperText={errors.phone}
        label={t("branches.create.phone")}
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          let value = input.value;

          value = value.replace(/[^\d\s+]/g, ""); // Permitimos numeros, espacios y +
          value = value.replace(/\s+/g, " "); // No permitimos mas de 1 espacio consecutivo

          // Si ingresa + que sea lo primero de la cadena
          value = value.replace(/(?!^)\+/g, "");
          if (value.includes("+") && value[0] !== "+") {
            value = "+" + value.replace(/\+/g, "");
          }

          input.value = value;
        }}
        variant="standard"
        disabled={disabled}
        sx={textFieldStyles}
      />

      {/* República Dominicana NO requiere: persona jurídica/física, CUIT/CUIL/Cédula, AFIP, provincia ni ciudad */}

      {withSplitTip && (
        <div className="w-full flex justify-between items-center">
          <div className="flex flex-col ">
            <label htmlFor="equalSplit">
              {t("branches.create.with-equal-split")}
            </label>
            <FormHelperText
              sx={{
                fontFamily: "Montserrat",
              }}
            >
              {t("branches.edit.split-helper")}
            </FormHelperText>
          </div>
          <Switch
            id="equalSplitTip"
            name="equalSplitTip"
            checked={branchSettings.equalSplitTip}
            onChange={(e) => handleSwitchChange(e, "branchConfig")}
            disabled={disabled}
            sx={switchStyles}
          />
        </div>
      )}

      <div className="w-full flex justify-between items-center">
        <div className="flex flex-col ">
          <label htmlFor="onlyRatings" className="text-black">
            {t("branches.create.only-review")}
          </label>
          <FormHelperText
            sx={{
              fontFamily: "Montserrat",
            }}
          >
            {t("branches.create.only-review-note")}
          </FormHelperText>
        </div>
        <Switch
          id="onlyRatings"
          name="onlyRatings"
          checked={branchSettings.onlyRatings}
          onChange={(e) => handleSwitchChange(e, "branchConfig")}
          disabled={true} // Siempre deshabilitado para República Dominicana
          sx={switchStyles}
        />
      </div>

      <div className="w-full flex flex-col relative">
        <div className="flex items-center gap-0.5">
          <span
            className="text-black font-normal text-base"
            style={{ fontFamily: "Montserrat" }}
          >
            {t("branches.create.google.title")}
          </span>
          <Tooltip
            title={
              <p style={{ fontFamily: "Montserrat" }}>
                {t("branches.create.google.tooltip")}
              </p>
            }
            placement="top"
          >
            <IconButton size="small" tabIndex={-1} sx={{ p: 0.25 }}>
              <InfoIcon
                sx={{
                  fill: "#fff",
                  height: 16,
                  width: 16,
                }}
              />
            </IconButton>
          </Tooltip>
        </div>
        <TextField
          type="text"
          fullWidth
          disabled={disabled}
          placeholder="https://g.page/your-business/review"
          value={branchSettings.googleReviewLink ?? ""}
          onChange={(e) =>
            handleBranchSettingsChange(
              e as unknown as React.ChangeEvent<HTMLInputElement>
            )
          }
          variant="standard"
          name="googleReviewLink"
          sx={textFieldStyles}
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open('https://support.google.com/business/answer/3474122?hl=es-419', '_blank');
          }}
          className="text-blue-600 hover:underline text-sm w-fit mt-1 text-left bg-transparent border-none p-0 cursor-pointer font-normal"
          style={{ zIndex: 1000 }}
        >
          {t("branches.create.google.link")}
        </button>
      </div>
    </div>
  );
}

